import { useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState, AppDispatch } from "@/store";
import {
  loginUser,
  logout,
  clearError,
  initializeAuth,
} from "@/store/slices/authSlice";

interface UserPermissions {
  [key: string]: boolean | number | string;
}

interface PlanInfo {
  type: "free_trial" | "basic" | "professional" | "enterprise" | "custom";
  max_users: number;
  unlimited_users: boolean;
  features: {
    dashboard: boolean;
    advanced_reports: boolean;
    pivot_tables: boolean;
    custom_funnels: boolean;
    api_access: boolean;
    storage_gb: number;
  };
}

export const useAuth = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const auth = useSelector((state: RootState) => state.auth);

  // DEBUG: Monitorear estado del usuario y sus permisos
  useEffect(() => {
    if (auth.user) {
      console.group("👤 [useAuth] Estado de Usuario Actualizado");
      console.log("🆔 User ID:", auth.user.id);
      console.log("🏷️ Rol:", auth.user.role?.name);
      console.log("🔐 Permisos cargados:", auth.user.role?.permissions);
      console.log("🏢 Empresa:", auth.company?.slug);
      console.groupEnd();
    }
  }, [auth.user, auth.company]);

  const getGroupedPermissions = useCallback(() => {
    if (!auth.user?.role?.permissions) return {};
    const permissions = auth.user.role.permissions;
    const grouped: Record<string, Record<string, boolean | number>> = {};

    if (Array.isArray(permissions)) {
      permissions.forEach((permission: string) => {
        const [resource, action] = permission.split(".");
        if (!grouped[resource]) grouped[resource] = {};
        grouped[resource][action] = true;
      });
    } else {
      Object.entries(permissions).forEach(([key, value]) => {
        const [resource, action] = key.split(".");
        if (!grouped[resource]) grouped[resource] = {};
        grouped[resource][action] = value === true;
      });
    }
    return grouped;
  }, [auth.user]);

  const canAny = useCallback(
    (resource: string): boolean => {
      if (!auth.user?.role?.permissions) return false;

      const permissions = auth.user.role.permissions;

      if (permissions["*"] === true || permissions["all"] === true) {
        return true;
      }

      const groupedPermissions = getGroupedPermissions();
      const resourcePermissions = groupedPermissions[resource];

      if (!resourcePermissions) return false;

      return Object.values(resourcePermissions).some(
        (value) => value === true || (typeof value === "number" && value > 0),
      );
    },
    [auth.user, getGroupedPermissions],
  );

  const getUserLimit = useCallback((): number | null => {
    if (Array.isArray(auth.user?.role?.permissions)) return null;
    const maxUsers = auth.user?.role?.permissions?.["users.max_count"];
    return typeof maxUsers === "number" ? maxUsers : null;
  }, [auth.user]);

  const getUserPlan = useCallback((): PlanInfo => {
    // Configuración única: Plan Enterprise para todos
    return {
      type: "enterprise",
      max_users: -1,
      unlimited_users: true,
      features: {
        dashboard: true,
        advanced_reports: true,
        pivot_tables: true,
        custom_funnels: true,
        api_access: true,
        storage_gb: 100,
      },
    };
  }, []);

  const canUseFeature = useCallback(
    (feature: string): boolean => {
      const featureMap: Record<string, keyof PlanInfo["features"]> = {
        dashboard: "dashboard",
        advanced_reports: "advanced_reports",
        pivot_tables: "pivot_tables",
        custom_funnels: "custom_funnels",
        api_access: "api_access",
        contacts: "dashboard",
        opportunities: "dashboard",
        products: "dashboard",
        quotations: "dashboard",
        reports: "advanced_reports",
        settings: "dashboard",
      };

      const featureKey = featureMap[feature];
      if (featureKey) {
        return true;
      }

      return can(feature);
    },
    [auth.user],
  );

  const checkUserLimit = useCallback(
    (currentUserCount: number): { canAdd: boolean; message?: string } => {
      return { canAdd: true };
    },
    [],
  );

  const getAvailableFeatures = useCallback((): string[] => {
    return [
      "dashboard",
      "advanced_reports",
      "pivot_tables",
      "custom_funnels",
      "api_access",
      "storage_gb",
    ];
  }, []);

  const canAccessRoute = useCallback(
    (
      route: string,
    ): { allowed: boolean; reason?: string; upgradeRequired?: boolean } => {
      if (!auth.isAuthenticated) {
        return { allowed: false, reason: "Debes iniciar sesión" };
      }

      const permissions = auth.user?.role?.permissions || {};

      const routePermissions: Record<string, string[]> = {
        "/dashboard": ["dashboard.view"],
        "/dashboard/advanced": ["dashboard.view", "reports.advanced"],
        "/contacts": ["contacts.read"],
        "/contacts/create": ["contacts.create"],
        "/contacts/:id/edit": ["contacts.update"],
        "/contacts/:id/delete": ["contacts.delete"],
        "/opportunities": ["opportunities.read"],
        "/opportunities/create": ["opportunities.create"],
        "/opportunities/:id/edit": ["opportunities.update"],
        "/reports": ["reports.view"],
        "/reports/advanced": ["reports.advanced"],
        "/reports/export": ["reports.export"],
        "/settings": ["settings.manage"],
        "/settings/billing": ["billing.manage"],
        "/users": ["users.read"],
        "/users/create": ["users.create"],
        "/users/:id/edit": ["users.update"],
        "/users/:id/delete": ["users.delete"],
        "/roles": ["roles.read"],
        "/roles/create": ["roles.create"],
        "/roles/:id/edit": ["roles.update"],
        "/products": ["products.read"],
        "/products/create": ["products.create"],
        "/products/:id/edit": ["products.update"],
      };

      const requiredPermissions = routePermissions[route] || [];

      if (requiredPermissions.length === 0) {
        return { allowed: true };
      }

      if (permissions["*"] === true || permissions.all === true) {
        return { allowed: true };
      }

      const hasAllPermissions = requiredPermissions.every((permission) => {
        return permissions[permission] === true;
      });

      if (!hasAllPermissions) {
        return {
          allowed: false,
          reason: "No tienes los permisos necesarios para acceder a esta ruta",
        };
      }

      return { allowed: true };
    },
    [auth.isAuthenticated, auth.user, getUserPlan],
  );

  const canCreateItem = useCallback(
    (
      itemType: "user" | "contact" | "opportunity",
      currentCount: number,
    ): boolean => {
      return true;
    },
    [],
  );

  const getPlanStats = useCallback(() => {
    const plan = getUserPlan();

    return {
      type: plan.type,
      max_users: plan.max_users,
      unlimited_users: plan.unlimited_users,
      features: plan.features,
      is_trial: false,
      is_enterprise: true,
      can_upgrade: false,
      upgrade_suggestions: [],
    };
  }, [getUserPlan]);

  const getUpgradeSuggestions = useCallback((): string[] => {
    return [];
  }, [getUserPlan]);

  const isFeatureAvailable = useCallback(
    (feature: string): boolean => {
      return canUseFeature(feature);
    },
    [canUseFeature],
  );

  const can = useCallback(
    (permission: string): boolean => {
      if (!auth.user?.role?.permissions) {
        return false;
      }

      const permissions = auth.user.role.permissions;

      if (permissions["*"] === true || permissions.all === true) {
        return true;
      }

      if (!Array.isArray(permissions) && permissions[permission] === true) {
        return true;
      }

      if (Array.isArray(permissions) && permissions.includes(permission)) {
        return true;
      }
      const [resource] = permission.split(".");
      if (resource) {
        if (
          !Array.isArray(permissions) &&
          (permissions[`${resource}.*`] === true ||
            permissions[`${resource}.all`] === true)
        ) {
          return true;
        }
        if (
          Array.isArray(permissions) &&
          (permissions.includes(`${resource}.*`) ||
            permissions.includes(`${resource}.all`))
        ) {
          return true;
        }
      }

      return false;
    },
    [auth.user],
  );

  const login = useCallback(
    (credentials: any) => {
      return dispatch(loginUser(credentials));
    },
    [dispatch],
  );

  const logoutWithRedirect = useCallback(() => {
    dispatch(logout());
    navigate("/login", { replace: true });
  }, [dispatch, navigate]);

  const clearAuthError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  const initAuth = useCallback(() => {
    return dispatch(initializeAuth());
  }, [dispatch]);

  const isNearUserLimit = useCallback(
    (currentCount: number, threshold = 2): boolean => {
      return false;
    },
    [],
  );

  return {
    user: auth.user,
    company: auth.company,
    token: auth.token,
    refreshToken: auth.refreshToken,
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    error: auth.error,

    login,
    logout: logoutWithRedirect,
    clearError: clearAuthError,
    initializeAuth: initAuth,

    getUserPlan,
    canUseFeature,
    checkUserLimit,
    getAvailableFeatures,
    canCreateItem,
    getPlanStats,
    isFeatureAvailable,

    can,
    canAccessRoute: (route: string) => canAccessRoute(route).allowed,
    canAccessRouteWithDetails: canAccessRoute,
    getUpgradeSuggestions,

    hasRole: useCallback(
      (role: string | string[]): boolean => {
        if (!auth.user?.role?.name) return false;
        const userRoleLower = auth.user.role.name.toLowerCase();
        if (Array.isArray(role)) {
          return role.some((r) => r.toLowerCase() === userRoleLower);
        }
        return userRoleLower === role.toLowerCase();
      },
      [auth.user],
    ),

    canAny,
    getRoleDisplayName: useCallback((): string => {
      if (!auth.user?.role?.name) return "Usuario";
      const roleMap: Record<string, string> = {
        Admin: "👑 Administrador",
        "Super Admin": "⭐ Super Administrador",
        Vendedor: "👤 Vendedor",
        Lector: "📖 Lector",
        Manager: "👔 Gerente",
      };
      return roleMap[auth.user.role.name] || auth.user.role.name;
    }, [auth.user]),

    getUserInitials: useCallback((): string => {
      if (!auth.user) return "US";
      if (auth.user.first_name || auth.user.last_name) {
        const firstName = auth.user.first_name || "";
        const lastName = auth.user.last_name || "";
        const initials = `${firstName.charAt(0)}${lastName.charAt(
          0,
        )}`.toUpperCase();
        if (initials.trim()) return initials;
      }
      if (auth.user.email) {
        return auth.user.email.substring(0, 2).toUpperCase();
      }
      return "US";
    }, [auth.user]),

    isOwner: useCallback((): boolean => {
      return auth.user?.is_owner === true;
    }, [auth.user]),

    getGroupedPermissions,
    getUserLimit,
    isNearUserLimit,

    isAdmin: useCallback((): boolean => {
      if (!auth.user?.role?.name) return false;
      const role = auth.user.role.name.toLowerCase();
      return role === "admin" || role === "super admin";
    }, [auth.user]),

    isSuperAdmin: useCallback((): boolean => {
      if (!auth.user?.role?.name) return false;
      return auth.user.role.name.toLowerCase() === "super admin";
    }, [auth.user]),

    canManageUsers: () => {
      const permissions = auth.user?.role?.permissions;
      if (
        permissions &&
        (permissions["*"] === true || permissions["all"] === true)
      ) {
        return true;
      }
      return canAny("users");
    },

    canManageRoles: () => {
      const permissions = auth.user?.role?.permissions;
      if (
        permissions &&
        (permissions["*"] === true || permissions["all"] === true)
      ) {
        return true;
      }
      return canAny("roles");
    },

    canManageContacts: () => {
      const permissions = auth.user?.role?.permissions;
      if (
        permissions &&
        (permissions["*"] === true || permissions["all"] === true)
      ) {
        return true;
      }
      return canAny("contacts");
    },

    canManageOpportunities: () => {
      const permissions = auth.user?.role?.permissions;
      if (
        permissions &&
        (permissions["*"] === true || permissions["all"] === true)
      ) {
        return true;
      }
      return canAny("opportunities");
    },

    canManageClientCompanies: () => {
      const permissions = auth.user?.role?.permissions;
      if (
        permissions &&
        (permissions["*"] === true || permissions["all"] === true)
      ) {
        return true;
      }
      return canAny("client_companies");
    },

    canViewReports: () => {
      const permissions = auth.user?.role?.permissions;
      if (
        permissions &&
        (permissions["*"] === true || permissions["all"] === true)
      ) {
        return true;
      }
      return canAny("reports");
    },

    canManageBilling: () => {
      const permissions = auth.user?.role?.permissions;
      if (
        permissions &&
        (permissions["*"] === true || permissions["all"] === true)
      ) {
        return true;
      }
      return can("billing.manage");
    },

    canManageSettings: () => {
      const permissions = auth.user?.role?.permissions;
      if (
        permissions &&
        (permissions["*"] === true || permissions["all"] === true)
      ) {
        return true;
      }
      return can("settings.manage");
    },
  };
};
